import Btn from '@/components/dataDisplay/Btn';
import CircleBtn from '@/components/dataDisplay/CircleBtn';
import Tooltip from '@/components/dataDisplay/Tooltip';
import {
  AMPLITUDE_ACTIONS,
  AMPLITUDE_EVENTS,
} from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { CSSStyles } from '@/modules/core/css/types/CSSStyles';
import {
  filterSelectedRecords,
  findRecordByField,
  GenericRecord,
} from '@/modules/core/data/record';
import { Campus } from '@/modules/core/data/types/Campus';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgram } from '@/modules/core/data/types/EducationalProgram';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { OnContextUpdate } from '@/modules/core/data/types/OnContextUpdate';
import { SetIsContextBuilderOpen } from '@/modules/core/data/types/SetIsContextBuilderOpen';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { selectStyles } from '@/modules/core/reactSelect/reactSelect';
import {
  CRITERIA_CATEGORIES_SOME_IS_IN_KEY,
  filterUnusedCriteriaRecords,
} from '@/modules/solutions/studentSolution';
import { filterUnusedContextEducationalPrograms } from '@/modules/solutions/studentSolutionContext';
import { isMobileDevice } from '@/utils/mobile';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import Icon from '@/common/components/dataDisplay/Icon';
import classnames from 'classnames';
import { ArrowUp, PlusCircle, Trash2 } from 'lucide-react';
import deepmerge from 'deepmerge';
import cloneDeep from 'lodash.clonedeep';
import filter from 'lodash.filter';
import get from 'lodash.get';
import map from 'lodash.map';
import remove from 'lodash.remove';
import size from 'lodash.size';
import React, {
  Fragment,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';

type Props = {
  contextFiltersSettings: ContextFiltersSettings;
  contextFilters: StudentSolutionContext;
  allStudentSolutions: StudentSolution[];
  allCampuses: Campus[];
  allEducationalPrograms: EducationalProgram[];
  allEducationalProgramSteps: EducationalProgramStep[];
  allCategories: StudentSolutionsCategory[];
  onContextUpdate: OnContextUpdate;
  setIsContextBuilderOpen: SetIsContextBuilderOpen;
}

/**
 * Dynamic context properties that can be changed by the end-user, trough the context builder.
 *
 * Do not contain all StudentSolutionContext keys, because some of them aren't meant to be changed.
 */
type DynamicContextProperty = keyof Omit<StudentSolutionContext, 'customer' | 'persona'>;

/**
 * Automatically handles adding/removing a given item from the contextFilters, and update the contextFilters.
 *
 * If the item is checked, then adds the item to the contextFilters.
 * If the item is not checked, then removes the item from the contextFilters.
 *
 * @param {StudentSolutionContext} contextFilters
 * @param {string} contextProperty
 * @param {StudentSolutionsCategory | Campus} item
 * @param {boolean} isChecked
 * @param {boolean} updateContext
 * @param onContextUpdate
 * @return {StudentSolutionContext}
 */
export const handleCheckboxChange = (contextFilters: StudentSolutionContext, contextProperty: DynamicContextProperty, item: StudentSolutionsCategory | Campus, isChecked: boolean, onContextUpdate: OnContextUpdate, updateContext = true): StudentSolutionContext => {
  let newContextItems: GenericRecord[] = contextFilters[contextProperty] || [];

  if (isChecked) {
    newContextItems.push(item);
  } else {
    remove(newContextItems, (contextItem: StudentSolutionsCategory | Campus) => {
      return contextItem?.id === item?.id;
    });

    if (size(newContextItems) === 0) {
      newContextItems = undefined;
    }
  }
  const newContext: StudentSolutionContext = {
    ...contextFilters,
    [contextProperty]: newContextItems,
  };

  if (updateContext) {
    onContextUpdate(newContext);
  }

  return newContext;
};

/**
 * Renders the builder when it's open.
 *
 * @return {JSX.Element}
 * @constructor
 */
const OpenBuilder: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    contextFiltersSettings,
    allStudentSolutions,
    allCampuses,
    allEducationalPrograms,
    allEducationalProgramSteps,
    allCategories,
    onContextUpdate,
    contextFilters,
    setIsContextBuilderOpen,
  } = props;
  const {
    educationalPrograms: contextEducationalPrograms,
    educationalProgramSteps: contextEducationalProgramSteps,
    campuses: contextCampuses,
    categories: contextCategories,
  } = contextFilters;
  const { t } = useTranslation();
  const theme: CustomerTheme = useTheme();
  const { primaryColor } = theme;
  const [isAddProgramButtonDisplayed, setIsAddProgramButtonDisplayed] = useState(false);
  const sanitizedContextFilters: StudentSolutionContext = filterUnusedContextEducationalPrograms(contextFilters);

  // Filter list of categories, by removing those that aren't actually used, so that they don't show up while doing nothing - Avoids UI clutter
  const allUsefulCategories: StudentSolutionsCategory[] = filterUnusedCriteriaRecords<StudentSolutionsCategory>(allStudentSolutions, allCategories, CRITERIA_CATEGORIES_SOME_IS_IN_KEY)
    .filter((studentSolutionsCategory: StudentSolutionsCategory) => !!studentSolutionsCategory?.ref); // Hide categories that don't have a ref
  const filterColumnSize: number = Math.floor(12 / contextFiltersSettings.enabledFiltersCount);

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <Row
          css={css`
            @media (max-width: 991.98px) {
              margin-top: 35px;
            }

            .content {
              align-content: center;

              &.offset:not(.contains-select) {
                margin-left: 60px;
              }

              &.justify-center:not(.contains-select) {
                display: flex;
                justify-content: center;
              }
            }

            .no-text-select {
              user-select: none; // Avoids user selecting text instead of toggling choice
            }

            .delete-program-btn {
              border: 1px solid white;
              border-radius: 20px;
              background-color: transparent;
              padding: 9px 7px 5px 12px;
              cursor: pointer;
              margin-top: 20px; // Vertical center

              @media (max-width: 991.98px) {
                left: -1px;
                position: absolute;
              }
            }

            .add-program-btn {
              background-color: ${primaryColor};
              cursor: pointer;
              border: 1px solid white;
              border-radius: 20px;
              margin-left: 15px;

              @media (max-width: 991.98px) {
                margin-top: 20px; // Vertical space between last filter and button
              }
            }

            .program-filter-separator {
              border: white 0.5px solid;
              width: 40px;
            }

            .row-spacing {
              @media (max-width: 991.98px) {
                margin-top: 50px; // Vertical space between each filter
              }
            }
          `}
        >
          {
            contextFiltersSettings.campusFilterEnabled && (
              <Amplitude
                eventProperties={(inheritedProps): GenericObject => ({
                  ...inheritedProps,
                  filter: {
                    ref: 'campus',
                    inputType: 'checkbox',
                    isMultiChoices: true,
                  },
                })}
              >
                {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
                  <Col
                    xs={12}
                    lg={filterColumnSize}
                  >
                    <div className={classnames('title')}>
                      {t('solutionsPage.contextBuilder.campusFilter.title', 'Campus')}
                      <CircleBtn>
                        {size(sanitizedContextFilters.campuses)}
                      </CircleBtn>
                    </div>
                    <div className={classnames('content offset justify-center')}>
                      <FormGroup check>
                        {
                          map(allCampuses, (campus: Campus) => {
                            return (
                              <div key={campus?.id}>
                                <Label
                                  className={'no-text-select'}
                                  check
                                >
                                  <Input
                                    type="checkbox"
                                    checked={!!findRecordByField(contextCampuses, campus?.id)}
                                    onChange={(): void => {
                                      // Toggle current "checked" state"
                                      const isChecked = !findRecordByField(contextCampuses, campus?.id);
                                      handleCheckboxChange(contextFilters, 'campuses', campus, isChecked, onContextUpdate);

                                      logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                        selection: {
                                          ref: campus.ref,
                                        },
                                        action: isChecked ? AMPLITUDE_ACTIONS.SELECT : AMPLITUDE_ACTIONS.REMOVE,
                                      });
                                    }}
                                  />
                                  {' '}
                                  {campus?.label}
                                </Label>
                              </div>
                            );
                          })
                        }
                      </FormGroup>
                    </div>
                  </Col>
                )}
              </Amplitude>
            )
          }

          {
            contextFiltersSettings.educationalProgramFilterEnabled && (
              <Amplitude
                eventProperties={(inheritedProps): GenericObject => ({
                  ...inheritedProps,
                  filter: {
                    ref: 'educationalPrograms',
                    inputType: 'select',
                    isMultiChoices: true,
                  },
                })}
              >
                {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
                  <Col
                    xs={12}
                    lg={filterColumnSize}
                  >
                    <div className={classnames('title')}>
                      {t('solutionsPage.contextBuilder.programFilter.title', 'Programmes')}
                      <CircleBtn>
                        {size(sanitizedContextFilters.educationalPrograms)}
                      </CircleBtn>
                    </div>
                    <div className={classnames('content contains-select')}>
                      <FormGroup check>
                        {
                          map(contextEducationalPrograms, (selectedEducationalProgram: EducationalProgram, i: number) => {
                            const selectedId = selectedEducationalProgram?.id;

                            // Display select input for the selected program that already are in the contextFilters
                            return (
                              <Fragment
                                key={selectedId}
                              >
                                <Fragment>
                                  <div
                                    className={'selects-container'}
                                  >
                                    <Row style={{ margin: 0 }}>
                                      <Col>
                                        <div className={'selected-programs'}>
                                          <Select
                                            className={'select select-simple'}
                                            value={selectedEducationalProgram}
                                            options={filterSelectedRecords(allEducationalPrograms, contextEducationalPrograms, selectedEducationalProgram)}
                                            onChange={(newSelectedEducationalProgram: EducationalProgram): void => {
                                              const newSelectedId = newSelectedEducationalProgram?.id;

                                              if (newSelectedId !== selectedId) {
                                                const newContext: StudentSolutionContext = {
                                                  ...contextFilters,
                                                };
                                                const selectedEducationalPrograms: EducationalProgram[] = get(contextFilters, 'educationalPrograms', []);

                                                // Remove the previously selected program from the content
                                                remove(selectedEducationalPrograms, (contextEducationalProgram: EducationalProgram) => {
                                                  return contextEducationalProgram?.id === selectedId;
                                                });

                                                // Remove all steps that belonged to the previously selected program, so that they don't stay hidden yet active in the contextFilters
                                                remove(get(contextFilters, 'educationalProgramSteps', []), (contextEducationalProgramStep: EducationalProgramStep) => {
                                                  return contextEducationalProgramStep?.program?.id === selectedId;
                                                });

                                                // If there are no more steps in the contextFilters, set to undefined so it doesn't get applied by the ConditionsMatcher filters later on
                                                if (size(contextFilters?.educationalProgramSteps) === 0) {
                                                  newContext.educationalProgramSteps = undefined;
                                                }

                                                selectedEducationalPrograms.push(newSelectedEducationalProgram);

                                                newContext.educationalPrograms = selectedEducationalPrograms;

                                                onContextUpdate(newContext);
                                                setIsAddProgramButtonDisplayed(false);

                                                logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                                  selection: {
                                                    ref: selectedEducationalProgram.ref,
                                                  },
                                                  action: AMPLITUDE_ACTIONS.SELECT,
                                                });
                                              }
                                            }}
                                            styles={selectStyles}
                                            placeholder={t('solutionsPage.contextBuilder.selectFilter.placeholder', 'Choisir')}
                                            noOptionsMessage={(inputValue: string): React.ReactNode => t('solutionsPage.contextBuilder.selectFilter.noMatchFound', 'Aucun résultat')}
                                            isSearchable={!isMobileDevice()} // Don't allow to search on mobile, avoids to display the keyboard
                                          />
                                        </div>
                                        <div className={'selected-steps'}>
                                          <Select
                                            className={'select select-multi'}
                                            isMulti={true}
                                            value={filter(contextEducationalProgramSteps, (step: EducationalProgramStep) => {
                                              return step?.program?.id === selectedId;
                                            })}
                                            options={filter(allEducationalProgramSteps, (step: EducationalProgramStep) => {
                                              return step?.program?.id === selectedId;
                                            })}
                                            getOptionValue={(step: EducationalProgramStep): string => {
                                              return step?.id;
                                            }}
                                            onChange={(allSelectedEducationalProgramSteps: EducationalProgramStep[]): void => {
                                              let hasStepsFromAnotherProgram = false;
                                              const otherProgramsSteps = [];

                                              map(contextFilters.educationalProgramSteps, (step: EducationalProgramStep) => {
                                                if (step?.program?.id !== selectedId) {
                                                  hasStepsFromAnotherProgram = true;
                                                  otherProgramsSteps.push(step);
                                                }
                                              });

                                              if (hasStepsFromAnotherProgram) {
                                                allSelectedEducationalProgramSteps = allSelectedEducationalProgramSteps || [];
                                              } else {
                                                if (size(allSelectedEducationalProgramSteps) === 0) {
                                                  allSelectedEducationalProgramSteps = undefined;
                                                }
                                              }

                                              const newCombinedProgramSteps = allSelectedEducationalProgramSteps ? otherProgramsSteps.concat(allSelectedEducationalProgramSteps) : undefined;
                                              const newContext: StudentSolutionContext = {
                                                ...contextFilters,
                                                educationalProgramSteps: newCombinedProgramSteps,
                                              };

                                              onContextUpdate(newContext);

                                              logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                                filter: {
                                                  ref: 'educationalProgramSteps',
                                                  inputType: 'select',
                                                  isMultiChoices: true,
                                                },
                                                selection: {
                                                  ref: map(newCombinedProgramSteps, 'ref'),
                                                },
                                                action: AMPLITUDE_ACTIONS.SELECT,
                                              });
                                            }}
                                            styles={deepmerge(selectStyles, {
                                              control: (styles): CSSStyles => {
                                                const selectedSteps: EducationalProgramStep[] = filter(contextEducationalProgramSteps, (step: EducationalProgramStep) => {
                                                  return step?.program?.id === selectedId;
                                                });

                                                return {
                                                  ...styles,
                                                  borderColor: !size(selectedSteps) ? 'red' : styles.borderColor,
                                                };
                                              },
                                            })}
                                            placeholder={t('solutionsPage.contextBuilder.selectFilter.placeholder', 'Choisir')}
                                            noOptionsMessage={(inputValue: string): React.ReactNode => t('solutionsPage.contextBuilder.selectFilter.noMatchFound', 'Aucun résultat')}
                                            closeMenuOnSelect={false}
                                            blurInputOnSelect={false} // Workaround to closeMenuOnSelect that actually works on Android Chrome - See https://github.com/JedWatson/react-select/issues/2771
                                            isSearchable={!isMobileDevice()} // Don't allow to search on mobile, avoids to display the keyboard
                                          />
                                        </div>
                                      </Col>
                                      <Col xs={1}>
                                        {
                                          !!size(contextEducationalPrograms) ? (
                                            <Tooltip
                                              overlay={<span>Supprimer ce programme</span>}
                                            >
                                              <Button
                                                className="delete-program-btn"
                                                onClick={(): void => {
                                                  // Delete both the selected program and all its selected steps
                                                  // Make sure they're set to "undefined" if there are none left (for both)
                                                  let newEducationalPrograms: EducationalProgram[] = cloneDeep(contextFilters.educationalPrograms);
                                                  let newEducationalProgramSteps: EducationalProgramStep[] = cloneDeep(contextFilters.educationalProgramSteps);

                                                  remove(newEducationalPrograms, (educationalProgram: EducationalProgram) => {
                                                    return educationalProgram.id === selectedEducationalProgram.id;
                                                  });

                                                  remove(newEducationalProgramSteps, (educationalProgramStep: EducationalProgramStep) => {
                                                    return educationalProgramStep.program.id === selectedEducationalProgram.id;
                                                  });

                                                  if (size(contextEducationalPrograms) === 0) {
                                                    newEducationalPrograms = undefined;
                                                  }

                                                  if (size(newEducationalProgramSteps) === 0) {
                                                    newEducationalProgramSteps = undefined;
                                                  }

                                                  onContextUpdate({
                                                    ...contextFilters,
                                                    educationalPrograms: newEducationalPrograms,
                                                    educationalProgramSteps: newEducationalProgramSteps,
                                                  });

                                                  logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                                    selection: {
                                                      ref: selectedEducationalProgram.ref,
                                                    },
                                                    action: AMPLITUDE_ACTIONS.REMOVE,
                                                  });
                                                }}
                                              >
                                                <Icon icon={Trash2} />
                                              </Button>
                                            </Tooltip>
                                          ) : null
                                        }
                                      </Col>
                                    </Row>
                                  </div>
                                  {
                                    // Display separator below (except for the last one)
                                    i < size(contextEducationalPrograms) - 1 && (
                                      <hr className={'program-filter-separator'} />
                                    )
                                  }
                                </Fragment>
                              </Fragment>
                            );
                          })
                        }

                        {
                          !size(contextEducationalPrograms) || isAddProgramButtonDisplayed ? (
                            // Displayed by default when no program was selected, and when a program has been selected in order to add another one
                            <Fragment>
                              <hr className={'program-filter-separator'} />
                              <Row style={{ margin: 0 }}>
                                <Col>
                                  <Select
                                    className={'select select-simple'}
                                    options={filterSelectedRecords(allEducationalPrograms, contextEducationalPrograms)}
                                    onChange={(selectedEducationalProgram: EducationalProgram): void => {
                                      const newContext: StudentSolutionContext = {
                                        ...contextFilters,
                                      };
                                      const selectedEducationalPrograms = get(contextFilters, 'educationalPrograms', []);
                                      selectedEducationalPrograms.push(selectedEducationalProgram);

                                      newContext.educationalPrograms = selectedEducationalPrograms;

                                      onContextUpdate(newContext);
                                      setIsAddProgramButtonDisplayed(false);

                                      logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                        selection: {
                                          ref: selectedEducationalProgram.ref,
                                        },
                                        action: AMPLITUDE_ACTIONS.SELECT,
                                      });
                                    }}
                                    styles={selectStyles}
                                    placeholder={t('solutionsPage.contextBuilder.selectFilter.placeholder', 'Choisir')}
                                    noOptionsMessage={(inputValue: string): React.ReactNode => t('solutionsPage.contextBuilder.selectFilter.noMatchFound', 'Aucun résultat')}
                                    isSearchable={!isMobileDevice()} // Don't allow to search on mobile, avoids to display the keyboard
                                  />
                                </Col>
                                <Col xs={1}>
                                  {/* Unused space meant to clone the behavior of the Row/Col above */}
                                </Col>
                              </Row>
                            </Fragment>
                          ) : null
                        }

                        {
                          !!size(contextEducationalPrograms) ? (
                            <Button
                              className={classnames('add-program-btn')}
                              onClick={(): void => {
                                setIsAddProgramButtonDisplayed(true);

                                logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                  action: 'added-new-empty',
                                  isAddProgramBtnAlreadyDisplayed: isAddProgramButtonDisplayed, // Track useless clicks (
                                });
                              }}
                            >
                              <Icon icon={PlusCircle} />
                              {t('solutionsPage.contextBuilder.programFilter.addProgramBtn', 'Ajouter un programme')}
                            </Button>
                          ) : null
                        }
                      </FormGroup>
                    </div>
                  </Col>
                )}
              </Amplitude>
            )
          }

          {
            contextFiltersSettings.categoryFilterEnabled && (
              <Amplitude
                eventProperties={(inheritedProps): GenericObject => ({
                  ...inheritedProps,
                  filter: {
                    ref: 'tag',
                    inputType: 'checkbox',
                    isMultiChoices: true,
                  },
                })}
              >
                {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
                  <Col
                    xs={12}
                    lg={filterColumnSize}
                    className={'row-spacing'}
                  >
                    <div className={classnames('title')}>
                      {t('solutionsPage.contextBuilder.categoryFilter.title', 'Catégories')}
                      <CircleBtn>
                        {size(contextFilters.categories)}
                      </CircleBtn>
                    </div>
                    <div className={classnames('content offset justify-center')}>
                      <FormGroup check>
                        {
                          map(allUsefulCategories, (category: StudentSolutionsCategory) => {
                            return (
                              <div key={category?.id}>
                                <Label
                                  className={'no-text-select'}
                                  check
                                >
                                  <Input
                                    type="checkbox"
                                    checked={!!findRecordByField(contextCategories, category?.id)}
                                    onChange={(): void => {
                                      // Toggle current "checked" state"
                                      const isChecked = !findRecordByField(contextCategories, category?.id);
                                      handleCheckboxChange(contextFilters, 'categories', category, isChecked, onContextUpdate);

                                      logEvent(AMPLITUDE_EVENTS.SOLUTIONS_FILTER_CHANGED, {
                                        selection: {
                                          ref: category.ref,
                                        },
                                        action: isChecked ? AMPLITUDE_ACTIONS.SELECT : AMPLITUDE_ACTIONS.REMOVE,
                                      });
                                    }}
                                  />
                                  <span className="checkmark"></span>
                                  {' '}
                                  {category?.label}
                                </Label>
                              </div>
                            );
                          })
                        }
                      </FormGroup>
                    </div>
                  </Col>
                )}
              </Amplitude>
            )
          }

          <Btn
            className={classnames('toggle-button')}
            mode={'primary-reverse'}
            onClick={(): void => {
              setIsContextBuilderOpen(false);

              logEvent('solutions-contextBuilderBtn-clicked', {
                action: AMPLITUDE_ACTIONS.CLOSE,
              });
            }}
          >
            <Icon icon={ArrowUp} />
            <span className={'hide-sm-md'}>
              {t('solutionsPage.closedBuilder.closeBtn', 'Réduire')}
            </span>
          </Btn>
        </Row>
      )}
    </Amplitude>
  );
};

export default OpenBuilder;
